import type { DirectoryNode } from '@mtm/drive/core';

import type { CreativeBlueprintNode } from '../../../../../shared/interfaces/project';

/**
 * One section tab of the drive project-stages panel. The backend no longer creates
 * section directories at project creation, so a blueprint section is listed even
 * when Drive has no directory for it yet (`directory === null`) — the first
 * sub-section dropped into it creates the directory.
 */
export interface StageSection {
  /** Stable across creation: the blueprint node uuid, or the directory uuid for a Drive-only section. */
  readonly key: string;
  readonly label: string;
  /** The Drive section directory; null until it is created. */
  readonly directory: DirectoryNode | null;
  /** The matching blueprint section; null for a Drive section absent from the blueprint. */
  readonly blueprintNode: CreativeBlueprintNode | null;
}

export function normalizeStageName(value: string | null | undefined): string {
  return (value ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function directoryLabel(dir: DirectoryNode): string {
  return dir.directoryDisplayName ?? dir.directoryName;
}

/**
 * Blueprint sections in blueprint `ordering`, each overridden by its matching Drive
 * directory when one exists, followed by the Drive sections that match no blueprint
 * section (in their Drive order). A directory matches a blueprint section by
 * normalized name or key, and is consumed by the first section it matches.
 */
export function mergeStageSections(
  driveSections: ReadonlyArray<DirectoryNode>,
  blueprintSections: ReadonlyArray<CreativeBlueprintNode>
): StageSection[] {
  const unmatched = [...driveSections];
  const fromBlueprint = [...blueprintSections]
    .sort((a, b) => a.ordering - b.ordering)
    .map((node): StageSection => {
      const index = unmatched.findIndex((dir) => matchesBlueprintNode(dir, node));
      const directory = index >= 0 ? unmatched.splice(index, 1)[0] : null;
      return {
        key: node.blueprintNodeUuid,
        label: directory ? directoryLabel(directory) : node.blueprintNodeName,
        directory,
        blueprintNode: node,
      };
    });
  const driveOnly = unmatched.map(
    (dir): StageSection => ({ key: dir.directoryUuid, label: directoryLabel(dir), directory: dir, blueprintNode: null })
  );
  return [...fromBlueprint, ...driveOnly];
}

function matchesBlueprintNode(dir: DirectoryNode, node: CreativeBlueprintNode): boolean {
  const targets = [node.blueprintNodeName, node.blueprintNodeKey].map(normalizeStageName).filter(Boolean);
  return [dir.directoryDisplayName, dir.directoryName]
    .map(normalizeStageName)
    .some((name) => !!name && targets.includes(name));
}
